/**
 * OfficerCard - the Teams-style profile card OfficerBadge opens where this Teams client does not
 * support Teams' own card (TeamsJS profile.showProfile is beta). Bill 2026-10-13: "include call
 * button as well ... mimic the Teams card styling as much as possible."
 *
 * Mirrors the Teams hover card: large photo with name / title / department and a Chat, Video,
 * Call action row; a scrolling body with "Send a quick message", the contact details (email,
 * mobile, work phone, office location) and "Reports to" - with its own Chat and Call buttons;
 * selecting the manager shows their card in place. Everything comes live from Entra
 * (/v3/config/staff-directory). Presence and calendar availability are left out until the API
 * has a source for them (Graph Presence / Calendars permissions).
 *
 * Bill 2026-10-14: every message says what it is about. When the card was opened from a cart,
 * a "Re:" strip names it; the quick message is SENT (POST /v3/staff/chat-message, as the
 * officer) with a "Re: <cart>" line linking back into the app. Until Teams chat sending is
 * switched on for the API, or if sending fails, the chat opens with the text and the reference
 * filled in instead - and the card says so. Chat buttons open the chat with the reference
 * pre-filled.
 */
import { useState, type FormEvent } from 'react';
import { Icon } from '@/components/primitives/Icon';
import { useApiClient } from '@/contexts/ApiClientContext';
import { profileFor, useStaffProfiles } from '@/hooks/useStaffProfiles';
import {
  openChatWithStaff,
  referenceLine,
  startCallWithStaff,
  startVideoCallWithStaff,
  type StaffMessageReference,
} from '@/services/teams';

export interface OfficerCardProps {
  upn: string;
  /** Shown until Entra answers, and when it has no name. */
  fallbackName: string;
  fallbackInitials: string;
  /** What the card was opened from (a cart); carried into every message. */
  reference?: StaffMessageReference | null;
  onClose: () => void;
}

type SendState = { kind: 'idle' } | { kind: 'sending' } | { kind: 'sent'; to: string } | { kind: 'drafted'; to: string };

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '')).toUpperCase();
}

function draft(text: string, ref: StaffMessageReference | null | undefined): string {
  return [text.trim(), referenceLine(ref)].filter(Boolean).join('\n\n');
}

export function OfficerCard({ upn: firstUpn, fallbackName, fallbackInitials, reference, onClose }: OfficerCardProps): JSX.Element {
  const client = useApiClient();
  // A small stack so "Reports to" can open the manager's card and Back returns.
  const [stack, setStack] = useState<string[]>([firstUpn]);
  const upn = stack[stack.length - 1] ?? firstUpn;
  const profiles = useStaffProfiles(stack);
  const p = profileFor(profiles, upn);
  const managerUpn = p?.manager?.upn ?? null;
  const managerProfiles = useStaffProfiles([managerUpn]);
  const mgr = profileFor(managerProfiles, managerUpn);
  const [msg, setMsg] = useState('');
  const [send, setSend] = useState<SendState>({ kind: 'idle' });

  const isFirst = stack.length === 1;
  const name = p?.name || (isFirst ? fallbackName : upn);
  const firstName = name.split(/\s+/)[0] || name;
  const initials = (isFirst && !p?.name ? fallbackInitials : initialsOf(name)) || '?';
  const email = p?.mail || upn;

  const act = (fn: () => Promise<void>) => (): void => { onClose(); void fn().catch(() => undefined); };
  const chat = (to: string) => act(() => openChatWithStaff(to, reference?.title ? draft('', reference) : undefined));

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const text = msg.trim();
    if (!text || send.kind === 'sending') return;
    setSend({ kind: 'sending' });
    let sent = false;
    try {
      const res = await client.retail.config.staffChatMessage({
        toUpn: upn, text, reference: reference?.title ? { title: reference.title, cartId: reference.cartId ?? null } : null,
      });
      sent = res.success === true;
    } catch {
      sent = false;
    }
    if (sent) {
      setMsg('');
      setSend({ kind: 'sent', to: firstName });
      return;
    }
    // Not sendable from here (permission pending or Teams refused): open the chat with the
    // message and the reference typed in, and say plainly that it still needs sending.
    setSend({ kind: 'drafted', to: firstName });
    void openChatWithStaff(upn, draft(text, reference)).catch(() => undefined);
  };

  return (
    <>
      <div className="officer-card__head">
        {!isFirst && (
          <button type="button" className="officer-card__back" aria-label="Back"
            onClick={() => { setStack((s) => s.slice(0, -1)); setSend({ kind: 'idle' }); }}>
            <Icon name="angle-small-left" size={16} />
          </button>
        )}
        <span className="officer-card__top">
          <span className="officer-card__photo">
            {p?.photo ? <img src={p.photo} alt="" width={88} height={88} /> : initials}
          </span>
          <span className="officer-card__who">
            <strong>{name}</strong>
            {p?.title && <span>{p.title}</span>}
            {p?.department && <span>{p.department}</span>}
          </span>
        </span>
        <span className="officer-card__icons">
          <button type="button" title="Chat" aria-label={`Chat with ${name}`} onClick={chat(upn)}>
            <Icon name="comment" size={18} />
          </button>
          <button type="button" title="Video call" aria-label={`Video call ${name}`} onClick={act(() => startVideoCallWithStaff(upn))}>
            <Icon name="video-camera" size={18} />
          </button>
          <button type="button" title="Call" aria-label={`Call ${name}`} onClick={act(() => startCallWithStaff(upn))}>
            <Icon name="phone-call" size={18} />
          </button>
        </span>
      </div>

      <div className="officer-card__body">
        {reference?.title && (
          <div className="officer-card__ref" title="Messages from this card include this reference and a link to it">
            <Icon name="shopping-cart" size={14} />
            <span><span className="officer-card__ref-label">Re:</span> {reference.title}</span>
          </div>
        )}
        <form className="officer-card__quick" onSubmit={(e) => void submit(e)}>
          <input
            type="text"
            value={msg}
            onChange={(e) => { setMsg(e.target.value); if (send.kind !== 'sending') setSend({ kind: 'idle' }); }}
            placeholder="Send a quick message"
            aria-label={`Quick message to ${name}`}
            maxLength={2000}
            disabled={send.kind === 'sending'}
          />
          <button type="submit" aria-label="Send" title="Send" disabled={!msg.trim() || send.kind === 'sending'}>
            <Icon name="paper-plane" size={16} />
          </button>
        </form>
        {send.kind === 'sending' && <div className="officer-card__status">Sending…</div>}
        {send.kind === 'sent' && (
          <div className="officer-card__status officer-card__status--ok" role="status">
            <Icon name="check" size={12} /> Sent to {send.to}{reference?.title ? ', with the reference' : ''}.
          </div>
        )}
        {send.kind === 'drafted' && (
          <div className="officer-card__status officer-card__status--warn" role="status">
            Not sent yet - your chat with {send.to} is open with the message filled in. Press Send there.
          </div>
        )}

        <div className="officer-card__section">
          <div className="officer-card__heading">Contact</div>
          <a className="officer-card__row" href={`mailto:${email}`} onClick={onClose}>
            <Icon name="envelope" size={16} /><span>{email}</span>
          </a>
          {p?.mobile && (
            <a className="officer-card__row" href={`tel:${p.mobile}`} onClick={onClose}>
              <Icon name="mobile-notch" size={16} /><span>{p.mobile}</span>
            </a>
          )}
          {p?.workPhone && (
            <a className="officer-card__row" href={`tel:${p.workPhone}`} onClick={onClose}>
              <Icon name="phone-call" size={16} /><span>{p.workPhone}</span>
            </a>
          )}
          {p?.office && (
            <span className="officer-card__row officer-card__row--text">
              <Icon name="marker" size={16} /><span>{p.office}</span>
            </span>
          )}
        </div>

        {p?.manager?.name && (
          <div className="officer-card__section">
            <div className="officer-card__heading">Reports to</div>
            <div className="officer-card__person-line">
              {managerUpn ? (
                <button type="button" className="officer-card__person" title={`Show ${p.manager.name}'s card`}
                  onClick={() => { setStack((s) => [...s, managerUpn]); setSend({ kind: 'idle' }); setMsg(''); }}>
                  <span className="officer-card__mini">
                    {mgr?.photo ? <img src={mgr.photo} alt="" width={32} height={32} /> : initialsOf(p.manager.name)}
                  </span>
                  <span className="officer-card__person-text">
                    <span>{p.manager.name}</span>
                    {p.manager.title && <small>{p.manager.title}</small>}
                  </span>
                </button>
              ) : (
                <span className="officer-card__person">
                  <span className="officer-card__mini">{initialsOf(p.manager.name)}</span>
                  <span className="officer-card__person-text">
                    <span>{p.manager.name}</span>
                    {p.manager.title && <small>{p.manager.title}</small>}
                  </span>
                </span>
              )}
              {managerUpn && (
                <span className="officer-card__person-actions">
                  <button type="button" title={`Chat with ${p.manager.name}`} aria-label={`Chat with ${p.manager.name}`} onClick={chat(managerUpn)}>
                    <Icon name="comment" size={16} />
                  </button>
                  <button type="button" title={`Call ${p.manager.name}`} aria-label={`Call ${p.manager.name}`}
                    onClick={act(() => startCallWithStaff(managerUpn))}>
                    <Icon name="phone-call" size={16} />
                  </button>
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
